<script setup lang="ts">
import type { AddressFormFieldData } from '@lib/shared';

import { INJECTION_KEYS } from '~/constants/injectionKeys';
import { type PlatformSaleItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: PlatformSaleItemEntity;
}

const { sale } = defineProps<Props>();

// EVENTS
const emit = defineEmits<{
    selectAddress: [number | undefined, string | undefined];
}>();

// MODEL
const isModalOpen = defineModel<boolean>('open', { required: true });

// COMPOSABLES
const { isMobile } = useResponsive();
const { tt } = useTypedI18n();
const salesContact = inject(INJECTION_KEYS.SALES_CONTACT, undefined);

// DATA
const price = ref<number>();
const destinationAddress = ref<AddressFormFieldData>();
const isAlternativeTransportModalOpen = ref(false);

// METHODS
const onComputePrice = (_price?: number): void => {
    price.value = _price;
};

const onConfirm = (): void => {
    emit('selectAddress', price.value, destinationAddress.value?.fullAddress);
    isModalOpen.value = false;
};

// The compute flow is a dead end for this destination: replace the address modal with the
// alternative-transport one instead of stacking them.
const onAlternativeTransportRequired = (): void => {
    isModalOpen.value = false;
    isAlternativeTransportModalOpen.value = true;
};
</script>

<template>
    <UModal v-model:open="isModalOpen" :fullscreen="isMobile" :title="tt('sdp.vas.logisticsModal.title')" v-bind="modalNoDescAriaFixupProps">
        <template #body>
            <div class="space-y-4">
                <p class="text-muted">{{ tt('sdp.vas.logisticsModal.description') }}</p>
                <VasComputePriceLogistics
                    v-model="destinationAddress"
                    :sale="sale"
                    name="logisticsDestinationAddress"
                    @compute-price="onComputePrice"
                    @alternative-transport-required="onAlternativeTransportRequired"
                />
                <UButton size="xl" :label="tt('sdp.vas.logisticsModal.confirm')" block :disabled="!destinationAddress" @click="onConfirm" />
            </div>
        </template>
    </UModal>

    <VasLogisticsAlternativeTransportModal v-model:open="isAlternativeTransportModalOpen" :sales-contact="salesContact" />
</template>
